function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { Typography } from "../Typography/index.js";
import { cn } from "../../common/helpers.js";
/*
 * Note: Width full and aspect ratio together will prevent content layout shift.
 * Object cover seemed like a sensible default; but can be overriden with slot props.
 */ const classesMedia = cva([
    'w-full',
    'object-cover'
], {
    variants: {
        aspectRatio: {
            '16:9': [
                'aspect-video'
            ],
            '4:3': [
                'aspect-4/3'
            ],
            '1:1': [
                'aspect-square'
            ],
            '3:4': [
                'aspect-3/4'
            ],
            'none': [
                'aspect-auto'
            ]
        },
        rounded: {
            true: [
                'rounded-xl'
            ],
            false: []
        }
    }
});
/**
 * Captioned Media - [API](https://scl.sharp.com/?path=/docs/components-captionedmedia) | [DSM](https://sharphealthcare.notion.site/Captioned-media-40471d1fec01491698e86af36433942d)
 *
 * @example
 *
 * <CaptionedMedia
 *  media={{ src: 'image.jpg' }}
 *  aspectRatio="4:3"
 *  caption="Cool image"
 *  rounded />
 *
 * <CaptionedMedia
 *  media={{ src: 'https://youtube.com/' }}
 *  caption={<>JSX captions <strong>work</strong></>} />
 *  slots={{ media: MyVideoPlayer }} />
 *
 */ const CaptionedMedia = /*#__PURE__*/ forwardRef((props, ref)=>{
    var _slotProps_caption;
    const { as: Root = 'figure', media = {}, caption, aspectRatio = '16:9', rounded = false, slots = {}, slotProps = {} } = props, rest = _object_without_properties(props, [
        "as",
        "media",
        "caption",
        "aspectRatio",
        "rounded",
        "slots",
        "slotProps"
    ]);
    var _slots_media;
    const Media = (_slots_media = slots.media) !== null && _slots_media !== void 0 ? _slots_media : 'img';
    const { className: mediaPropClassName } = media, mediaPropProps = _object_without_properties(media, [
        "className"
    ]);
    var _slotProps_media;
    const _ref = (_slotProps_media = slotProps.media) !== null && _slotProps_media !== void 0 ? _slotProps_media : {}, { className: mediaSlotPropsClassName } = _ref, mediaSlotProps = _object_without_properties(_ref, [
        "className"
    ]);
    const mediaProps = slots.media ? _object_spread({}, mediaSlotProps) : _object_spread({}, mediaPropProps, mediaSlotProps);
    var _slots_caption;
    const Caption = (_slots_caption = slots.caption) !== null && _slots_caption !== void 0 ? _slots_caption : Typography;
    var _slotProps_caption1;
    const _ref1 = (_slotProps_caption1 = slotProps.caption) !== null && _slotProps_caption1 !== void 0 ? _slotProps_caption1 : {}, { className: captionSlotPropsClassName } = _ref1, captionSlotProps = _object_without_properties(_ref1, [
        "className"
    ]);
    const captionDefaultProps = {
        as: 'figcaption',
        variant: 'small-body',
        align: 'left'
    };
    const captionProps = slots.caption ? captionSlotProps : _object_spread({}, captionDefaultProps, captionSlotProps);
    return /*#__PURE__*/ _jsxs(Root, _object_spread_props(_object_spread({}, rest), {
        ref: ref,
        children: [
            /*#__PURE__*/ _jsx(Media, _object_spread({
                className: cn(classesMedia({
                    aspectRatio,
                    rounded
                }), mediaSlotPropsClassName)
            }, mediaProps)),
            (caption !== null && caption !== void 0 ? caption : slotProps === null || slotProps === void 0 ? void 0 : (_slotProps_caption = slotProps.caption) === null || _slotProps_caption === void 0 ? void 0 : _slotProps_caption.children) && /*#__PURE__*/ _jsx(Caption, _object_spread_props(_object_spread({
                className: cn('mt-3 empty:hidden scl-rich-text', captionSlotPropsClassName)
            }, captionProps), {
                children: caption
            }))
        ]
    }));
});
export { CaptionedMedia };
